import { useState, useCallback } from 'react'
import {
  Box,
  Text,
  TextField,
  Inline,
  IconClose,
  Columns,
  Column,
  Skeleton,
  Button
} from '@machobear/design-system'
import { noop } from 'lodash'
import { useTranslation } from 'react-i18next'
import { usePaymentContext } from './payment-context'

type Props = {
  discount?: number
  isFetching?: boolean
  couponError: boolean
  onClear?: () => void
}

const CouponField = ({ discount, isFetching, couponError, onClear = noop }: Props) => {
  const { t } = useTranslation()
  const { coupon, setCoupon, couponApplyButtonId } = usePaymentContext()
  const [code, setCode] = useState(coupon)

  const applyDiscount = () => {
    setCoupon(code)
  }

  const removeDiscount = useCallback(() => {
    setCoupon('')
    setCode('')
    onClear()
  }, [setCoupon, onClear])

  if (isFetching) {
    return <Skeleton.Shape height={11} width={'100%'} />
  }
  const disableButton = !code || (couponError && code === coupon)

  return discount ? (
    <Box display="flex" alignItems="center" justifyContent="spaceBetween" width="full">
      <Inline space="xxsmall" alignY="center">
        <Text size="small" tone="secondary">
          {t('r.payment.edit.discount.label', 'Discount')}
        </Text>
        <Box
          background="navItem"
          borderRadius="full"
          paddingY="xxxsmall"
          paddingX="xxsmall"
          border="borderNeutralLight"
          display="flex"
          gap="xxxxsmall"
          alignItems="center"
          cursor="pointer"
        >
          <Text size="xsmall">{code}</Text>
          <Box onClick={removeDiscount} style={{ width: '16px', height: '16px' }}>
            <IconClose size="fill" />
          </Box>
        </Box>
      </Inline>
      <Text size="small" tone="secondary">
        -${discount}
      </Text>
    </Box>
  ) : (
    <Box display="flex" flexDirection="column" gap="xsmall" width="full">
      <Columns space="xsmall">
        <Column width="3/4">
          <TextField
            id="coupon"
            name="coupon"
            value={code}
            onChange={(e: any) => setCode(e.target.value)}
            label={t('l.promotions.coupon.label.text', 'Enter coupon code')}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                applyDiscount()
              }
            }}
            onClear={removeDiscount}
            tone={couponError ? 'critical' : undefined}
          />
        </Column>
        <Column width="1/4">
          <Button
            id={couponApplyButtonId}
            variant="ghost"
            onClick={applyDiscount}
            disabled={disableButton}
            loading={isFetching}
            size="large"
          >
            {t('l.promotions.coupon.apply.button.text', 'Apply')}
          </Button>
        </Column>
      </Columns>
      {couponError && (
        <Text tone="critical" size="small">
          {t('l.promotions.coupon.invalid.text', 'Invalid or expired coupon code')}
        </Text>
      )}
    </Box>
  )
}

export default CouponField
